import React, { useEffect, useRef, useState } from "react";
import { useSearchBox } from 'react-instantsearch';
import range from 'lodash/range';
import { Button, H3, H5, IconCollection, IconSpinner, Modal } from '../../components';
import { createSlug } from "../../util/urlHelpers";
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { makeStyles } from 'tss-react/mui';
import { FormattedMessage, useIntl } from 'react-intl';
import CreateInvoiceReductionProviderModal from '../EventDashboardPage/CreateInvoiceReductionProviderModal';
import { getUserBidAcceptedTransactionsWithListing } from '../../util/api';
import CreateInvoiceProviderModal from "../EventDashboardPage/CreateInvoiceProviderModal";
import CreateInvoiceOvertimeProviderModal from "../EventDashboardPage/CreateInvoiceOvertimeProviderModal";
import { REQUEST } from "../../transactions/transaction";
import { getTransactionTypeData } from "../CheckoutPage/CheckoutPageTransactionHelpers";
import { pathByRouteName } from "../../util/routes";
import { getEventTypeLabel } from "../../util/data";
import classNames from "classnames";
import { capitalize } from "lodash";
import ProviderEventDetailsTab from './EventDetailsTab/ProviderEventDetailsTab';
import ProviderCompletedEventsTab from './CompletedEventsTab/ProviderCompletedEventsTab';
import ProviderInvoicesTab from './InvoicesTab/ProviderInvoicesTab';
import EventsDashboardContent from './EventsDashboardContent/EventsDashboardContent';
import { onUpdateEventBidListing, updateAlgoliaData, updateTransactionMetadata } from '../../util/api';

import css from './EventProviderDashboardPageForm.module.css';


const onManageDisableScrolling = (componentId, scrollingDisabled = true) => {
    // We are just checking the value for now
    console.log('Toggling Modal - scrollingDisabled currently:', componentId, scrollingDisabled);
};


/**
 * Get transactions by listingId
 * @param {string} listingId
 * @param {Object} initiatedTransactions
 * @returns {Array<{ transactionId: string, amount: string }>}
 */
function getTransactionByListingId(listingId, initiatedTransactions) {
    // Guard clauses
    if (!listingId || typeof listingId !== 'string') return null;
    if (!initiatedTransactions || typeof initiatedTransactions !== 'object') {
        return null;
    }

    for (const [transactionId, value] of Object.entries(initiatedTransactions)) {
        // Skip invalid entries safely
        if (
            !transactionId ||
            !value ||
            typeof value !== 'object' ||
            !value.listingIdUuid
        ) {
            continue;
        }

        if (value.listingIdUuid === listingId) {
            return {
                transactionId,
                amount: value?.amount ?? null,
            };
        }
    }

    return null;
}



const InstantSearchEventsSearchInput = props => {
    const { placeholder, value, onValueChange, debounceMs = 300 } = props;
    const { query, refine } = useSearchBox();

    const debounceTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, []);

    // Highlight matching text
    const highlightText = (text, searchQuery) => {
        if (!searchQuery || !text) return text;
        
        const regex = new RegExp(`(${searchQuery})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => {
            if (part.toLowerCase() === searchQuery.toLowerCase()) {
                return <span key={index} className={css.searchHighlight}>{part}</span>;
            }
            return part;
        });
    };

    return (
        <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={e => {
                const nextValue = e.target.value;
                onValueChange(nextValue);

                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }

                debounceTimerRef.current = setTimeout(() => {
                    refine(nextValue);
                }, debounceMs);
            }}
        />
    );
};

/**
 * Returns an array containing page numbers and possible ellipsis '…' characters.
 *
 * @param {Number} page - current page
 * @param {Number} totalPages - total page count
 * @return {Array}
 */
const getPageNumbersArray = (page, totalPages) => {
    // Create array of numbers: [1, 2, 3, 4, ..., totalPages]
    const numbersFrom1ToTotalPages = range(1, totalPages + 1);
    return numbersFrom1ToTotalPages
        .filter(v => {
            // Filter numbers that are next to current page and pick also first and last page
            // E.g. [1, 4, 5, 6, 9], where current page = 5 and totalPages = 9.
            return v === 1 || Math.abs(v - page) <= 1 || v === totalPages;
        })
        .reduce((newArray, p) => {
            // Create a new array where gaps between consecutive numbers is filled with ellipsis character
            // E.g. [1, '…', 4, 5, 6, '…', 9], where current page = 5 and totalPages = 9.
            const isFirstPageOrNextToCurrentPage = p === 1 || newArray[newArray.length - 1] + 1 === p;
            return isFirstPageOrNextToCurrentPage ? newArray.concat([p]) : newArray.concat(['\u2026', p]);
        }, []);
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    formControl: {
        margin: theme.spacing(3),
    },
}));



const handleCreateInvoice = (
    submitting,
    setSubmitting,
    props,
    values,
    setIsCreateInvoiceProviderModalOpen,
    options = {}
) => {
    if (submitting) {
        return;
    }
    setSubmitting(true);
    const { closeImmediately = true, invoiceType: invoiceTypeOption } = options;
    if (closeImmediately) {
        setIsCreateInvoiceProviderModalOpen(false);
    }


    const {
        history,
        config,
        routes: routeConfiguration,
        currentUser,
        onMakeOffer,
        onSubmitCallback,
        onMakeTransitionNormal,
    } = props;

    const {
        selectedTransaction = {},
        selectedTransactionId,
        additionalExpenses = [],
        documents = [],
        eventUpdate = [],
        overtimeDetails = null,
        reductionDetails = null,
        actor = 'provider',
        providerDefaultMessage = null,
        customerDefaultMessage = null,
    } = values;

    const serializeMoneyMaybe = m => {
        if (!m) return null;
        const amount = m?.amount;
        const currency = m?.currency;
        const amountInt = parseInt(amount);
        const hasAmount = Number.isFinite(amountInt);
        const hasCurrency = typeof currency === 'string' && currency.length > 0;
        return hasAmount && hasCurrency ? { amount: amountInt, currency } : null;
    };

    const buildOvertimeDetailsMaybe = od => {
        if (!od) return null;

        const next = {
            overtimeHours: od?.overtimeHours,
            overtimeRate: serializeMoneyMaybe(od?.overtimeRate),
            totalOvertimeCost: serializeMoneyMaybe(od?.totalOvertimeCost),
            overtimeReason: od?.overtimeReason,
        };

        const hasHours = typeof next.overtimeHours === 'string' && next.overtimeHours.trim().length > 0;
        const hasReason = typeof next.overtimeReason === 'string' && next.overtimeReason.trim().length > 0;
        const hasRate = !!next.overtimeRate;
        const hasTotal = !!next.totalOvertimeCost;

        return hasHours || hasReason || hasRate || hasTotal ? next : null;
    };

    const buildReductionDetailsMaybe = rd => {
        if (!rd) return null;

        const deductions = Array.isArray(rd?.deductions)
            ? rd.deductions
                .map(d => ({
                    reason: d?.reason,
                    amount: serializeMoneyMaybe(d?.amount),
                }))
                .filter(d => d.reason || d.amount)
            : [];

        const next = {
            deductions,
            totalReduction: serializeMoneyMaybe(rd?.totalReduction),
            reductionReason: rd?.reductionReason,
        };

        const hasDeductions = deductions.length > 0;
        const hasTotal = !!next.totalReduction;
        const hasReason = typeof next.reductionReason === 'string' && next.reductionReason.trim().length > 0;

        return hasDeductions || hasTotal || hasReason ? next : null;
    };

    const overtimeDetailsMaybe = buildOvertimeDetailsMaybe(overtimeDetails);
    const reductionDetailsMaybe = buildReductionDetailsMaybe(reductionDetails);

    // Resolve the invoice type. Explicit option wins; otherwise infer from the details present.
    const invoiceType =
        invoiceTypeOption ||
        (reductionDetailsMaybe ? 'reduction' : overtimeDetailsMaybe ? 'overtime' : 'expense');
    const isOvertimeInvoice = invoiceType === 'overtime';
    const isReductionInvoice = invoiceType === 'reduction';
    const isExpenseInvoice = invoiceType === 'expense';

    const { listing, protectedData = {}, author: listingAuthor, transactionId: bidAcceptedTransactionId } = selectedTransaction;

    const invoiceData = {
        invoiceType,
        actor,
        listingId: listing?.id?.uuid,
        selectedTransactionId,
        additionalExpenses,
        documents,
        eventUpdate,
        ...(overtimeDetailsMaybe ? { overtimeDetails: overtimeDetailsMaybe } : {}),
        ...(reductionDetailsMaybe ? { reductionDetails: reductionDetailsMaybe } : {}),
    };

    const biddingAmount = parseInt(protectedData?.finalBid?.bidAmount);
    const biddingCurrency = config.currency;

    const expensesCost = additionalExpenses.reduce((acc, expense) => {
        const v = parseInt(expense?.cost?.amount);
        return acc + (Number.isFinite(v) ? v : 0);
    }, 0);

    const overtimeCost = (() => {
        if (!isOvertimeInvoice) return 0;
        const v = parseInt(overtimeDetailsMaybe?.totalOvertimeCost?.amount);
        return Number.isFinite(v) ? v : 0;
    })();

    const reductionCost = (() => {
        if (!isReductionInvoice) return 0;
        const v = parseInt(reductionDetailsMaybe?.totalReduction?.amount);
        return Number.isFinite(v) ? v : 0;
    })();

    // The offer amount is always a positive magnitude. `invoiceType` (persisted in
    // protectedData) tells the server-side pricing whether this magnitude increases
    // the final charge (overtime / expense) or credits it (reduction).
    const totalCost = isReductionInvoice ? reductionCost : expensesCost + overtimeCost;
    // const transactionId = selectedTransactionId;
    const transactionId = null;

    const listingId = listing?.id;
    const listingType = 'negotiation';
    const transactionProcessAlias = 'default-negotiation/release-1';
    const unitType = 'request';


    const makeOfferParamsJson = JSON.stringify({
        listingId: listingId,
        protectedData: {
            ...protectedData,
            isNegotiationFlow: true,
            isPaymentFlow: false,
            isBiddingFlow: false,
            invoiceData,
            ...(providerDefaultMessage ? { providerDefaultMessage: 'Hello' } : {}),
            ...(customerDefaultMessage ? { customerDefaultMessage: 'Hello' } : {}),
            ...getTransactionTypeData(listingType, unitType, config),
        },
        offerInSubunits: totalCost,
        currency: biddingCurrency,
    }, null, 2)


    // console.log("makeOfferParamsJson :: ", makeOfferParamsJson)



    // These are the inquiry parameters for the (one and only) transition
    const makeOfferParams = {
        listingId: listingId,
        protectedData: {
            ...protectedData,
            isNegotiationFlow: true,
            isPaymentFlow: false,
            isBiddingFlow: false,
            invoiceType,
            isOvertimeInvoice,
            isReductionInvoice,
            isExpenseInvoice,
            invoiceData,
            ...(providerDefaultMessage ? { providerDefaultMessage: 'Hello' } : {}),
            ...(customerDefaultMessage ? { customerDefaultMessage: 'Hello' } : {}),
            ...getTransactionTypeData(listingType, unitType, config),
        },
        offerInSubunits: totalCost,
        currency: biddingCurrency,
    };
    const isPrivilegedTransition = true;

    // This makes a single transition directly to the API endpoint
    // (unlike in the payment-related processes, where call is proxied through the server to make privileged transition)
    return onMakeOffer(makeOfferParams, transactionProcessAlias, transactionId, isPrivilegedTransition)
        .then(async transaction => {
            setSubmitting(false);
            onSubmitCallback();

            setIsCreateInvoiceProviderModalOpen(false);

            // before navigating to transaction page update the bid transaction to invoice creation.
            onMakeTransitionNormal(bidAcceptedTransactionId, 'transition/invoice-generated', {});

            // Link the new invoice tx back to the main payment tx so its transitions
            // can be shown in the merged ActivityFeed on the TransactionPage.
            const newInvoiceTxId = transaction?.id?.uuid;
            if (newInvoiceTxId && bidAcceptedTransactionId) {
              updateTransactionMetadata({
                transactionId: bidAcceptedTransactionId,
                metadata: { invoiceTxIds: [newInvoiceTxId] },
              }).catch(e => console.warn('[invoice] failed to link invoice tx to payment tx:', e?.message));
            }

            // Update listing publicData and Algolia with transactionStatus: 'invoice-generated'
            try {
                await onUpdateEventBidListing({
                    eventListingId: listing.id,
                    transactionId: bidAcceptedTransactionId,
                    publicData: { transactionStatus: 'invoice-generated' }
                });

                await updateAlgoliaData({
                    indexName: process.env.REACT_APP_ALGOLIA_EVENT_LISTING_INDEX || 'event',
                    objectID: listing.id,
                    transactionStatus: 'invoice-generated'
                });
            } catch (updateError) {
                console.error('Failed to update listing publicData and Algolia after invoice creation', updateError);
            }

            // Redirect to the transaction page matching the user's actual role on this tx.
            // Invoice initiate uses transition/make-offer (customer actor in process.edn),
            // so the provider user may be stored as customer on the transaction.
            const currentUserUuid = currentUser?.id?.uuid;
            const txCustomerUuid = transaction?.customer?.id?.uuid;
            const txProviderUuid = transaction?.provider?.id?.uuid;
            const transactionPage =
                txCustomerUuid === currentUserUuid
                    ? 'OrderDetailsPage'
                    : txProviderUuid === currentUserUuid
                      ? 'SaleDetailsPage'
                      : 'SaleDetailsPage';
            const txDetailsPath = pathByRouteName(transactionPage, routeConfiguration, {
                id: transaction?.id?.uuid,
            });

            history.push(txDetailsPath);
        })
        .catch(err => {
            console.error(err);
            setSubmitting(false);
        });
};

const EventProviderDashboardPageForm = props => {
    const {
        activeTab,
        events = [],
        currentPage = 1,
        onPageChange,
        itemsPerPage = 10,
        totalItems = 0,
        useInstantSearch = false,
        isSearching = false,
        intl,
        history,
        onManageDisableScrolling,
        initiatedTransactions,
        currentUserId,
        isModalOpen,
        setIsModalOpen,
        config,
        eventTypes
    } = props;

    const [isBillReductionProviderModalOpen, setIsBillReductionProviderModalOpen] = useState(false);
    const [isBillReductionProviderModalLoading, setIsBillReductionProviderModalLoading] = useState(false);
    const [billReductionProviderTransactions, setBillReductionProviderTransactions] = useState(null);
    const [isCreateInvoiceProviderModalOpen, setIsCreateInvoiceProviderModalOpen] = useState(false);
    const [isCreateInvoiceOvertimeProviderModalOpen, setIsCreateInvoiceOvertimeProviderModalOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState({
        reduction: false,
        overtime: false,
        expense: false,
        requestPayment: false
    });

    // 1️⃣ Unique transaction IDs (object keys)
    const transactionIds = Object.keys(initiatedTransactions || {});

    // 2️⃣ Unique listing IDs
    const listingIds = [
        ...new Set(
            Object.values(initiatedTransactions || {}).map(
                (txn) => txn.listingIdUuid
            )
        ),
    ];

    const classes = useStyles();

    const allEvents = events && events.length ? events : [];
    // Filter events that don't have invoice-generated status
    // const filteredEvents = allEvents.length ? allEvents?.filter(event => event.rawHit?.transactionStatus !== 'invoice-generated') : [];
    const filteredEvents = allEvents.length ? allEvents : [];
    // Pagination calculations (server-side: events already paged)
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const shouldShowPagination = totalItems > itemsPerPage;

    // Events are already for the current page
    const paginatedEvents = filteredEvents;

    // Page navigation handlers
    const handlePreviousPage = () => {
        if (currentPage > 1 && onPageChange) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages && onPageChange) {
            onPageChange(currentPage + 1);
        }
    };

    const handlePageClick = (page) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    const handleCheckboxChange = event => {
        const { name, checked } = event.target;

        // Only allow selecting one option at a time.
        setSelectedOptions(prev => {
            const next = Object.keys(prev).reduce((acc, k) => {
                acc[k] = false;
                return acc;
            }, {});

            // Allow unchecking the currently selected option.
            next[name] = Boolean(checked);
            return next;
        });
    };

    const handleModifyBillProceed = async () => {
        setIsModalOpen(false);

        if (selectedOptions.overtime) {
            setIsCreateInvoiceOvertimeProviderModalOpen(true);
        } else if (selectedOptions.expense || selectedOptions.requestPayment) {
            setIsCreateInvoiceProviderModalOpen(true);
        } else if (selectedOptions.reduction) {
            setIsBillReductionProviderModalOpen(true);
        }

        // If we've already loaded the data once, reuse it when reopening the modal.
        if (billReductionProviderTransactions && Array.isArray(billReductionProviderTransactions?.transactions)) {
            return;
        }

        if (!currentUserId) {
            console.error('Missing currentUserId: cannot fetch transactions for bill reduction modal');
            setBillReductionProviderTransactions({ transactions: [], meta: {} });
            return;
        }

        try {
            setIsBillReductionProviderModalLoading(true);
            const resp = await getUserBidAcceptedTransactionsWithListing({ userId: currentUserId, perPage: 100, page: 1 });
            const data = resp?.data || { transactions: [], meta: {} };
            setBillReductionProviderTransactions(data);
        } catch (e) {
            console.error('Failed to fetch bidAccepted transactions with listing', e);
            setBillReductionProviderTransactions({ transactions: [], meta: {} });
        } finally {
            setIsBillReductionProviderModalLoading(false);
        }
    };

    // Bill Reduction is create/display/negotiate-complete, but a reduction is a
    // credit that cannot settle through the standard Stripe payment path until
    // escrow (Phase 5) lands. Keep it behind a flag so the un-settleable flow is
    // not exposed by default. Flip REACT_APP_ENABLE_BILL_REDUCTION=true to enable.
    const isBillReductionEnabled = process.env.REACT_APP_ENABLE_BILL_REDUCTION === 'true';

    const canProceed =
        selectedOptions.requestPayment ||
        selectedOptions.overtime ||
        selectedOptions.expense ||
        (isBillReductionEnabled && selectedOptions.reduction);

    const handleCreateInvoiceProviderSubmit = (values) => {
        return handleCreateInvoice(submitting, setSubmitting, props, values, setIsCreateInvoiceProviderModalOpen, {
            invoiceType: 'expense',
        });
    };

    const handleBillReductionProviderSubmit = (values) => {
        return handleCreateInvoice(submitting, setSubmitting, props, values, setIsBillReductionProviderModalOpen, {
            invoiceType: 'reduction',
        });
    };

    const handleOvertimeInvoiceProviderSubmit = values => {

        return handleCreateInvoice(
            submitting,
            setSubmitting,
            props,
            values,
            setIsCreateInvoiceOvertimeProviderModalOpen,
            { closeImmediately: false, invoiceType: 'overtime' }
        );
    };

    // Generate page numbers array
    const pageNumbers = shouldShowPagination ? getPageNumbersArray(currentPage, totalPages) : [];

    // Add the modals and other JSX that should be rendered for all tabs

    // Return only the modals for all tabs
    return (
        <>
            {/* 
              Tab Content Rendering
              - Renders different content based on the active tab
              - Dashboard: Shows events table and cards with search/pagination
              - Events: Shows provider event details tab
              - CompletedEvents: Shows completed events tab
              - Fallback: Shows tab name for unknown tabs
            */}
            {activeTab === 'Dashboard' && (
                <EventsDashboardContent
                    events={events}
                    paginatedEvents={paginatedEvents}
                    totalItems={totalItems}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    shouldShowPagination={shouldShowPagination}
                    pageNumbers={pageNumbers}
                    handlePreviousPage={handlePreviousPage}
                    handleNextPage={handleNextPage}
                    handlePageClick={handlePageClick}
                    useInstantSearch={useInstantSearch}
                    isSearching={isSearching}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    currentUserId={currentUserId}
                    listingIds={listingIds}
                    initiatedTransactions={initiatedTransactions}
                    getTransactionByListingId={getTransactionByListingId}
                    eventTypes={eventTypes}
                />
            )}
            
            {activeTab === 'Events' && (
                <ProviderEventDetailsTab currentUserId={currentUserId} />
            )}
            
            {activeTab === 'CompletedEvents' && (
                <ProviderCompletedEventsTab currentUserId={currentUserId} />
            )}
            
            {activeTab === 'RPFS' && (
                <div className={css.eventDashboardFormContainer}>
                    <div className={css.eventDashboardFormHeader}>
                        <div className={css.eventDashboardFormTitleContainer}>
                            <h1 className={css.eventDashboardFormTitle}>RPFS</h1>
                        </div>
                    </div>
                    <div className={css.emptyState}>
                        <div className={css.emptyStateContent}>
                            <div className={css.emptyStateIcon}>
                                <IconCollection icon="documentIcon" />
                            </div>
                            <div className={css.emptyStateText}>
                                <p className={css.emptyStateTitle}>No RPFS found</p>
                                <p className={css.emptyStateDescription}>Your RFP submissions will appear here once available.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'Invoices' && (
                <ProviderInvoicesTab currentUserId={currentUserId} />
            )}

            {/* 
              Modify Bill Modal
              - Allows providers to modify their billing for events
              - Options: bill reduction (credit to client), overtime rate, expense coverage
              - Each option opens its dedicated invoice modal
              - Modal opens when provider clicks "Create Invoice" action button
            */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onManageDisableScrolling={onManageDisableScrolling}
                className={css.modifyBillModal}
            >
                <div>
                    <H3>
                        <FormattedMessage id='EventDashboardPage.modifyBillHeading' />
                    </H3>
                    <H5>
                        <FormattedMessage id='EventDashboardPage.modifyBillSubHeading' />
                    </H5>

                    <div>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormGroup>
                                {/* Overtime Rate Option - Allows providers to request overtime payment */}
                                <FormControlLabel
                                    control={<Checkbox onChange={handleCheckboxChange} name="overtime" checked={selectedOptions.overtime} />}
                                    label={intl.formatMessage({ id: 'EventDashboardPage.modifyBillLabelOvertimeRate' })}
                                />

                                {/* Expense Coverage Option - Provider requests reimbursement for expenses */}
                                <FormControlLabel
                                    control={<Checkbox onChange={handleCheckboxChange} name="expense" checked={selectedOptions.expense} />}
                                    label={intl.formatMessage({ id: 'EventDashboardPage.modifyBillLabelExpenseCoverage' })}
                                />

                                {/* Request Payment Option - Currently Commented Out */}
                                {/* <FormControlLabel
                                    control={<Checkbox onChange={handleCheckboxChange} name="requestPayment" checked={selectedOptions.requestPayment} />}
                                    label={intl.formatMessage({ id: 'EventDashboardPage.modifyBillLabelRequestPayment' })}
                                /> */}
                            </FormGroup>
                        </FormControl>
                    </div>

                    {/* Modal Action Buttons */}
                    <div className={css.modifyBillButtonsContainer}>
                        <Button
                            className={css.modifyBillCancelButton}
                            onClick={() => setIsModalOpen(false)}
                        >
                            {intl.formatMessage({ id: 'EventDashboardPage.modifyBillCancelButton' })}
                        </Button>

                        <Button
                            className={css.modifyBillProceedButton}
                            onClick={handleModifyBillProceed}
                            disabled={!canProceed}
                        >
                            {intl.formatMessage({ id: 'EventDashboardPage.modifyBillProceedButton' })}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* 
              Bill Reduction Provider Modal (Overtime Bill)
              - Opens when provider selects overtime rate option in modify bill modal
              - Allows provider to submit overtime hours and rate details
              - Handles bill reduction calculations based on overtime work
              - Shows transaction history related to bill modifications
            */}
            <Modal
                isOpen={isBillReductionProviderModalOpen}
                onClose={() => setIsBillReductionProviderModalOpen(false)}
                onManageDisableScrolling={onManageDisableScrolling}
                containerClassName={css.billReductionModalContainer}
                contentClassName={css.billReductionModalContent}
                className={css.billReductionModal}
            >
                <CreateInvoiceReductionProviderModal
                    intl={intl}
                    onSubmit={handleBillReductionProviderSubmit}
                    submitting={submitting}
                    onClose={() => setIsBillReductionProviderModalOpen(false)}
                    eventData={events && events.length > 0 ? events[0] : null}
                    isLoading={isBillReductionProviderModalLoading}
                    config={config}
                    transactionsData={billReductionProviderTransactions}
                    actor="provider"
                />
            </Modal>

            {/* 
              Create Invoice Overtime Provider Modal
              - Opens for creating overtime invoices
              - Allows providers to submit detailed overtime billing information
              - Includes overtime hours, rates, and additional charges
              - Integrates with payment processing system
            */}
            <Modal
                isOpen={isCreateInvoiceOvertimeProviderModalOpen}
                onClose={() => setIsCreateInvoiceOvertimeProviderModalOpen(false)}
                onManageDisableScrolling={onManageDisableScrolling}
                containerClassName={css.billReductionModalContainer}
                contentClassName={css.billReductionModalContent}
                className={css.billReductionModal}
            >
                <CreateInvoiceOvertimeProviderModal
                    intl={intl}
                    onSubmit={handleOvertimeInvoiceProviderSubmit}
                    submitting={submitting}
                    onClose={() => setIsCreateInvoiceOvertimeProviderModalOpen(false)}
                    eventData={events && events.length > 0 ? events[0] : null}
                    isLoading={isBillReductionProviderModalLoading}
                    config={config}
                    transactionsData={billReductionProviderTransactions}
                />
            </Modal>

            {/* 
              Create Invoice Provider Modal
              - Opens for general invoice creation
              - Allows providers to create standard invoices for completed work
              - Includes billing details, line items, and payment terms
              - Handles invoice submission and tracking
            */}
            <Modal
                isOpen={isCreateInvoiceProviderModalOpen}
                onClose={() => setIsCreateInvoiceProviderModalOpen(false)}
                onManageDisableScrolling={onManageDisableScrolling}
                containerClassName={css.billReductionModalContainer}
                contentClassName={css.billReductionModalContent}
                className={css.billReductionModal}
            >
                <CreateInvoiceProviderModal
                    intl={intl}
                    onSubmit={handleCreateInvoiceProviderSubmit}
                    submitting={submitting}
                    onClose={() => setIsCreateInvoiceProviderModalOpen(false)}
                    eventData={events && events.length > 0 ? events[0] : null}
                    isLoading={isBillReductionProviderModalLoading}
                    config={config}
                    transactionsData={billReductionProviderTransactions}
                />
            </Modal>
        </>
    );
};

export default EventProviderDashboardPageForm;